import moment, { Moment } from 'moment';
import * as React from 'react';
import { isInclusivelyBeforeDay, SingleDatePicker } from 'react-dates';
import { FormGroup, Label } from 'reactstrap';
import Input from 'reactstrap/lib/Input';
import { localMoment } from 'util/Time';
import { SingleDateRangePresets } from '../providers/Constants';
import { RangedPresetValue, SingleDateFilterEditProps } from '../providers/Types';
import { FiltersListAddButton } from './index';
import FilterEditRequiredValidation from './FilterEditRequiredValidation';

interface SingleDateFilterEditState {
	currentValue: string;
	displayValidationError: boolean;
	focused: boolean;
	customFromValue: moment.Moment | null;
	customToValue: moment.Moment | null;
}
/**
 * Configures a single date value within a filter
 */
class SingleDateFilterEdit extends React.PureComponent<SingleDateFilterEditProps, SingleDateFilterEditState> {
	constructor(props: Readonly<SingleDateFilterEditProps>) {
		super(props);

		if (props.value === undefined) {
			this.state = {
				currentValue: '',
				displayValidationError: false,
				focused: false,
				customFromValue: null,
				customToValue: null,
			};
		} else {
			this.state = {
				currentValue: props.value[0].preset === undefined ? 'custom' : props.value[0].preset,
				displayValidationError: false,
				focused: false,
				customFromValue: null,
				customToValue: props.value[0].preset === undefined ? localMoment(props.value[0].toValue) : null,
			};
		}
	}

	/**
	 * Update local state when the controlled select dropdown is updated
	 */
	private onSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ currentValue: e.currentTarget.value });
	};

	/**
	 * Update local state when the controlled datepicker is updated
	 */
	private onDateChange = (date: Moment) => {
		this.setState({ customToValue: date.startOf('day') });
	};

	private focusOnChange = (isFocused: boolean | null) => {
		this.setState({
			focused: !!isFocused,
		});
	};

	private getValuesFromProps(): RangedPresetValue[] {
		// always return empty so the filters don't get stacked.
		return [];
	}

	/**
	 * Adds the value currently in state to the list of values selected.
	 *
	 * @private
	 * @memberof EnumDropDownFilterEdit
	 */
	private handleSearch = () => {
		const currentValue = this.state.currentValue;
		const values = this.getValuesFromProps();
		let currentValueDateRange: RangedPresetValue;

		if (currentValue.toString() === 'custom') {
			currentValueDateRange = {
				fromValue: this.state.customFromValue,
				toValue: this.state.customToValue,
			};
		} else {
			const presetFn = SingleDateRangePresets[currentValue.toString()];
			if (!presetFn) {
				console.warn('Preset not found:', currentValue);
				return;
			}

			currentValueDateRange = presetFn();
		}

		const isEmpty = currentValueDateRange.fromValue === null && currentValueDateRange.toValue === null;
		const validationError = !this.props.filter.nullable && isEmpty;

		this.setState({
			displayValidationError: validationError,
		});

		const isUnique =
			values.length === 0 ||
			values.findIndex(
				(value) =>
					value.preset === currentValueDateRange.preset ||
					(value.fromValue === currentValueDateRange.fromValue && value.toValue === currentValueDateRange),
			) === -1;

		this.setState({
			displayValidationError: validationError,
		});

		if (!validationError && isUnique) {
			this.props.onChange([...values, currentValueDateRange], false);
		}
	};

	/**
	 * Allow users to hit enter to add new values.
	 */
	private handleOnKeyDown = (e: React.KeyboardEvent) => {
		if (e.which === 13) {
			e.preventDefault();
			this.handleSearch();
		}
	};

	public render() {
		let showPastDates: boolean = true;
		let showFutureDates: boolean = true;

		if (this.props.filter.options != null && this.props.filter.options.dateParams != null) {
			if (this.props.filter.options.dateParams.showPastDates != undefined) {
				showPastDates = this.props.filter.options.dateParams.showPastDates;
			}
			if (this.props.filter.options.dateParams.showFutureDates != undefined) {
				showFutureDates = this.props.filter.options.dateParams.showFutureDates;
			}
		}

		const values = this.getValuesFromProps();
		const availableDateRanges = Object.keys(SingleDateRangePresets).filter((key) => {
			return !values.some((value) => value.preset === key);
		});

		return (
			<FormGroup>
				<Label for="date-select" className="visually-hidden">
					Select date
				</Label>
				<Input
					id="date-select"
					type="select"
					autoFocus
					value={this.state.currentValue}
					onKeyDown={this.handleOnKeyDown}
					onChange={this.onSelectChange}
					className=""
					autoComplete="off"
				>
					<option value="" />
					{availableDateRanges.map((preset, index) => {
						const presetValue = SingleDateRangePresets[preset]();
						return (
							<option key={index} value={preset}>
								{presetValue.displayName || presetValue.preset}
							</option>
						);
					})}
					<option value="custom">Custom Date...</option>
				</Input>
				{this.state.currentValue.toString() === 'custom' ? (
					<SingleDatePicker
						id={this.props.filter.name}
						showDefaultInputIcon
						showClearDate
						small
						date={this.state.customToValue}
						focused={this.state.focused}
						onDateChange={this.onDateChange}
						onFocusChange={({ focused }) => {
							this.focusOnChange(focused);
						}}
						isOutsideRange={
							showPastDates && !showFutureDates
								? (day) => !isInclusivelyBeforeDay(day, moment())
								: showPastDates && showFutureDates
								? () => false
								: undefined
						}
					/>
				) : null}
				<div className="filters-popover__actions">
					<FiltersListAddButton onClick={this.handleSearch} />
				</div>
				{this.state.displayValidationError ? (
					<FilterEditRequiredValidation messageOverride="Field is required" />
				) : null}
			</FormGroup>
		);
	}
}
export default SingleDateFilterEdit;
